const BTL_SCRIPT_ID = "proma-btl-sdk"
const BTL_SCRIPT_SRC = "https://btl.proma.ai/script.js"
const BTL_HUD_SCRIPT_ID = "proma-btl-hud"
const BTL_HUD_SCRIPT_SRC = "https://btl.proma.ai/btl-hud.js"
const DEFAULT_BTL_SITE_ID = "clientmobileapp"
const DEFAULT_BTL_SERVER_URL = "https://btl.proma.ai"

let scriptPromise
let session
let activeSiteId
let mutationObserver
let rediscoverTimer

const getPromaBTL = () =>
  typeof window !== "undefined" &&
  (window.PrimaBTL || window.PromaBTL || window.PromaBTLSDK)

const loadBTLScript = () => {
  if (typeof document === "undefined") return Promise.resolve(null)

  const existing = getPromaBTL()
  if (existing) return Promise.resolve(existing)

  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const loadedScript = document.getElementById(BTL_SCRIPT_ID)
    if (loadedScript) {
      loadedScript.addEventListener("load", () => resolve(getPromaBTL()))
      loadedScript.addEventListener("error", reject)
      return
    }

    const script = document.createElement("script")
    script.id = BTL_SCRIPT_ID
    script.src = BTL_SCRIPT_SRC
    script.async = true
    script.onload = () => resolve(getPromaBTL())
    script.onerror = reject
    document.head.appendChild(script)
  })

  return scriptPromise
}

// The visitor-insight HUD (btl-hud.js) is admin-gated: it renders only when the
// browser is opted in with ?btl_admin=1 (persisted in localStorage), and exposes a
// no-op window.BTLHud otherwise. We only inject it for opted-in browsers so normal
// visitors never download it; the HUD script still owns the on/off/token logic, so
// we load it whenever the param is present (any value) or the flag is stored.
const shouldLoadHUD = () => {
  if (typeof window === "undefined") return false
  try {
    if (new URLSearchParams(window.location.search).has("btl_admin"))
      return true
    return localStorage.getItem("btl_admin") != null
  } catch (e) {
    return false
  }
}

const loadHUDScript = () => {
  if (typeof document === "undefined" || !shouldLoadHUD()) return
  if (window.BTLHud || document.getElementById(BTL_HUD_SCRIPT_ID)) return

  const script = document.createElement("script")
  script.id = BTL_HUD_SCRIPT_ID
  script.src = BTL_HUD_SCRIPT_SRC
  script.async = true
  // Rocket Loader reorders script tags and would break the HUD behind Cloudflare.
  script.setAttribute("data-cfasync", "false")
  document.head.appendChild(script)
}

export const createBTLSiteId = businessId => {
  const explicitSiteId = process.env.GATSBY_BTL_SITE_ID
  if (explicitSiteId) return explicitSiteId

  return businessId
    ? `${DEFAULT_BTL_SITE_ID}-${businessId}`
    : DEFAULT_BTL_SITE_ID
}

export const createBTLConfig = businessId => {
  const siteId = createBTLSiteId(businessId)
  const serverUrl = process.env.GATSBY_BTL_SERVER_URL || DEFAULT_BTL_SERVER_URL

  return {
    siteId,
    serverUrl,
  }
}

export const startBTL = async ({ businessId, onToken, onScore } = {}) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null
  }

  const promaBTL = await loadBTLScript()
  if (!promaBTL || !promaBTL.createBTLSession) return null

  // Admin-only visitor-insight HUD; no-ops for normal visitors (see loadHUDScript).
  loadHUDScript()

  const { siteId, serverUrl } = createBTLConfig(businessId)
  document.body.dataset.pzSiteId = siteId

  if (promaBTL.setBTLServerUrl) {
    promaBTL.setBTLServerUrl(serverUrl)
  }

  if (!session || activeSiteId !== siteId) {
    if (session && session.destroy) session.destroy()

    activeSiteId = siteId
    session = promaBTL.createBTLSession({
      onToken,
      // Live prediction pushed over the SDK WebSocket -> drives the HUD.
      onScore: score => {
        if (typeof onScore === "function") onScore(score)
        if (typeof window !== "undefined" && window.BTLHud) {
          window.BTLHud.update(score)
        }
      },
      config: {
        siteId,
      },
    })
  } else if (session.rediscoverZones) {
    session.rediscoverZones()
  }

  if (!mutationObserver && window.MutationObserver && document.body) {
    mutationObserver = new MutationObserver(() => {
      clearTimeout(rediscoverTimer)
      rediscoverTimer = setTimeout(rediscoverBTL, 250)
    })

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    })
  }

  return session
}

export const rediscoverBTL = () => {
  if (session && session.rediscoverZones) {
    session.rediscoverZones()
  }
}

export const deferBTLCTAAction = action => {
  if (typeof window === "undefined") {
    action()
    return
  }

  window.setTimeout(action, 250)
}

export const stopBTL = () => {
  if (session && session.destroy) {
    session.destroy()
  }

  session = null
  activeSiteId = null
  clearTimeout(rediscoverTimer)

  if (mutationObserver) {
    mutationObserver.disconnect()
    mutationObserver = null
  }
}
